import { useAuth0 } from '#~/auth/index.js';
import { useRemove, useUpdate } from '#~/device/components/hooks.js';
import { raiseError } from '#~/notifications/index.js';
import { useSelectedAudio } from '#~/room/hooks.js';
import type { Room } from '#~/room/index.js';
import { useAddAudio, useSettings } from '../hooks.js';
import type { Wiring } from './types.js';
import type { Device } from '@studio/models/device';
import { useLatestFn } from '@studio/react-utils/hooks';

/** @internal */
export const useWiring = (devices: Room['devices']): Wiring => {
  const { getAccessTokenSilently } = useAuth0();

  const [selectedId, handleSelect] = useSelectedAudio();
  const handleAdd = useAddAudio();
  const handleUpdate = useUpdate();
  const remove = useRemove({ type: 'audio' });
  const settings = useSettings();

  const handleRemove = useLatestFn((deviceId: Device['id']) => {
    const device = devices[deviceId];

    if (device) {
      remove(device);
    }
  });

  const handleRename = useLatestFn(async (deviceId: Device['id'], nextName: string) => {
    const token = await getAccessTokenSilently();

    handleUpdate(deviceId, { name: nextName }, token).catch((error) =>
      raiseError(error, { actionable: true })
    );
  });

  const handleSettings = useLatestFn((deviceId: Device['id']) => {
    const device = devices[deviceId];

    if (device) {
      settings(device);
    }
  });

  return {
    handleAdd,
    handleRemove,
    handleRename,
    handleSelect,
    handleSettings,
    selectedId
  };
};
